// src/store/slices/admin/statSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from '../../../services/apiClient';
import { API_ENDPOINTS } from '../../../constants/apiEndpoints';

export const getStats = createAsyncThunk(
    'stats/getStats',
    async (params = {}, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) throw new Error('No token available');
            const response = await apiClient.get(API_ENDPOINTS.STATS, { params });
            const { status, message, data } = response.data;
            if (status === 200) {
                return { success: true, message: message || 'Stats retrieved', data };
            } else {
                return rejectWithValue({ message: message || 'Failed to fetch stats', status });
            }
        } catch (error) {
            let errorMessage = 'Failed to fetch stats';
            if (error.response) {
                errorMessage = error.response.data?.message || error.response.data?.error || errorMessage;
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }
            return rejectWithValue({ message: errorMessage, status: error.response?.status || 500 });
        }
    }
);

export const getStat = createAsyncThunk(
    'stats/getStat',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) throw new Error('No token available');
            const response = await apiClient.get(API_ENDPOINTS.STAT(id));
            const { status, message, data } = response.data;
            if (status === 200) {
                return { success: true, message: message || 'Stat retrieved', stat: data };
            } else {
                return rejectWithValue({ message: message || 'Failed to fetch stat', status });
            }
        } catch (error) {
            let errorMessage = 'Failed to fetch stat';
            if (error.response) {
                errorMessage = error.response.data?.message || error.response.data?.error || errorMessage;
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }
            return rejectWithValue({ message: errorMessage, status: error.response?.status || 500 });
        }
    }
);

export const createStat = createAsyncThunk(
    'stats/createStat',
    async (data, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) throw new Error('No token available');
            const response = await apiClient.post(API_ENDPOINTS.STATS + '/create', data);
            const { status, message, data: responseData } = response.data;
            if (status === 201 || status === 200) {
                return { success: true, message: message || 'Stat created', stat: responseData };
            } else {
                return rejectWithValue({ message: message || 'Failed to create stat', status });
            }
        } catch (error) {
            let errorMessage = 'Failed to create stat';
            if (error.response) {
                errorMessage = error.response.data?.message || error.response.data?.error || errorMessage;
                if (error.response.data?.errors) {
                    const errorMessages = Object.values(error.response.data.errors).flat().join(', ');
                    errorMessage = errorMessages || errorMessage;
                }
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }
            return rejectWithValue({ message: errorMessage, status: error.response?.status || 500 });
        }
    }
);

export const updateStat = createAsyncThunk(
    'stats/updateStat',
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) throw new Error('No token available');
            const response = await apiClient.put(API_ENDPOINTS.STAT(id), data);
            const { status, message, data: responseData } = response.data;
            if (status === 200) {
                return { success: true, message: message || 'Stat updated', stat: responseData };
            } else {
                return rejectWithValue({ message: message || 'Failed to update stat', status });
            }
        } catch (error) {
            let errorMessage = 'Failed to update stat';
            if (error.response) {
                errorMessage = error.response.data?.message || error.response.data?.error || errorMessage;
                if (error.response.data?.errors) {
                    const errorMessages = Object.values(error.response.data.errors).flat().join(', ');
                    errorMessage = errorMessages || errorMessage;
                }
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }
            return rejectWithValue({ message: errorMessage, status: error.response?.status || 500 });
        }
    }
);

export const deleteStat = createAsyncThunk(
    'stats/deleteStat',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) throw new Error('No token available');
            const response = await apiClient.delete(API_ENDPOINTS.STAT(id));
            const { status, message } = response.data;
            if (status === 200) {
                return { success: true, message: message || 'Stat deleted', id };
            } else {
                return rejectWithValue({ message: message || 'Failed to delete stat', status });
            }
        } catch (error) {
            let errorMessage = 'Failed to delete stat';
            if (error.response) {
                errorMessage = error.response.data?.message || error.response.data?.error || errorMessage;
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }
            return rejectWithValue({ message: errorMessage, status: error.response?.status || 500 });
        }
    }
);

export const toggleStatStatus = createAsyncThunk(
    'stats/toggleStatus',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) throw new Error('No token available');
            const response = await apiClient.patch(API_ENDPOINTS.STAT_TOGGLE(id));
            const { status, message, data } = response.data;
            if (status === 200) {
                return { success: true, message: message || 'Status toggled', id, is_active: data?.is_active };
            } else {
                return rejectWithValue({ message: message || 'Failed to toggle status', status });
            }
        } catch (error) {
            let errorMessage = 'Failed to toggle status';
            if (error.response) {
                errorMessage = error.response.data?.message || error.response.data?.error || errorMessage;
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }
            return rejectWithValue({ message: errorMessage, status: error.response?.status || 500 });
        }
    }
);

export const exportStats = createAsyncThunk(
    'stats/export',
    async (params = {}, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) throw new Error('No token available');
            const response = await apiClient.get(API_ENDPOINTS.STATS_EXPORT, { params });
            const { status, message, data } = response.data;
            if (status === 200) {
                return { success: true, message: message || 'Exported successfully', data };
            } else {
                return rejectWithValue({ message: message || 'Failed to export', status });
            }
        } catch (error) {
            let errorMessage = 'Failed to export';
            if (error.response) {
                errorMessage = error.response.data?.message || error.response.data?.error || errorMessage;
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }
            return rejectWithValue({ message: errorMessage, status: error.response?.status || 500 });
        }
    }
);

// =============================================
// SLICE
// =============================================

const initialState = {
    items: [],
    pagination: { current_page: 1, last_page: 1, per_page: 15, total: 0 },
    currentItem: null,
    isLoading: false,
    isSubmitting: false,
    error: null,
    successMessage: null,
    apiMessage: null,
};

const statSlice = createSlice({
    name: 'stats',
    initialState,
    reducers: {
        clearError: (state) => { state.error = null; },
        clearSuccessMessage: (state) => { state.successMessage = null; },
        clearApiMessage: (state) => { state.apiMessage = null; },
        clearState: (state) => {
            state.items = [];
            state.pagination = { current_page: 1, last_page: 1, per_page: 15, total: 0 };
            state.currentItem = null;
            state.isLoading = false;
            state.isSubmitting = false;
            state.error = null;
            state.successMessage = null;
            state.apiMessage = null;
        },
        clearCurrentItem: (state) => { state.currentItem = null; },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getStats.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getStats.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload;
                const responseData = payload.data || {};
                state.items = responseData.data || [];
                state.pagination = responseData.meta || { current_page: 1, last_page: 1, per_page: 15, total: 0 };
                state.successMessage = payload.message || 'Stats retrieved';
                state.apiMessage = payload.message || 'Stats retrieved';
                state.error = null;
            })
            .addCase(getStats.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch stats';
                state.apiMessage = action.payload?.message || 'Failed to fetch stats';
                state.successMessage = null;
                state.items = [];
                state.pagination = { current_page: 1, last_page: 1, per_page: 15, total: 0 };
            })
            .addCase(getStat.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getStat.fulfilled, (state, action) => {
                state.isLoading = false;
                state.currentItem = action.payload.stat || null;
                state.successMessage = action.payload.message || 'Stat retrieved';
                state.apiMessage = action.payload.message || 'Stat retrieved';
                state.error = null;
            })
            .addCase(getStat.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch stat';
                state.apiMessage = action.payload?.message || 'Failed to fetch stat';
                state.successMessage = null;
                state.currentItem = null;
            })
            .addCase(createStat.pending, (state) => {
                state.isSubmitting = true;
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(createStat.fulfilled, (state, action) => {
                state.isSubmitting = false;
                state.isLoading = false;
                const newItem = action.payload.stat;
                if (newItem) {
                    state.items = [newItem, ...state.items];
                }
                state.successMessage = action.payload.message || 'Stat created';
                state.apiMessage = action.payload.message || 'Stat created';
                state.error = null;
            })
            .addCase(createStat.rejected, (state, action) => {
                state.isSubmitting = false;
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to create stat';
                state.apiMessage = action.payload?.message || 'Failed to create stat';
                state.successMessage = null;
            })
            .addCase(updateStat.pending, (state) => {
                state.isSubmitting = true;
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(updateStat.fulfilled, (state, action) => {
                state.isSubmitting = false;
                state.isLoading = false;
                const updated = action.payload.stat;
                if (updated) {
                    state.items = state.items.map(item => item.id === updated.id ? updated : item);
                    if (state.currentItem?.id === updated.id) {
                        state.currentItem = updated;
                    }
                }
                state.successMessage = action.payload.message || 'Stat updated';
                state.apiMessage = action.payload.message || 'Stat updated';
                state.error = null;
            })
            .addCase(updateStat.rejected, (state, action) => {
                state.isSubmitting = false;
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to update stat';
                state.apiMessage = action.payload?.message || 'Failed to update stat';
                state.successMessage = null;
            })
            .addCase(deleteStat.pending, (state) => {
                state.isSubmitting = true;
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(deleteStat.fulfilled, (state, action) => {
                state.isSubmitting = false;
                state.isLoading = false;
                const id = action.payload.id;
                state.items = state.items.filter(item => item.id !== id);
                if (state.currentItem?.id === id) state.currentItem = null;
                state.successMessage = action.payload.message || 'Stat deleted';
                state.apiMessage = action.payload.message || 'Stat deleted';
                state.error = null;
            })
            .addCase(deleteStat.rejected, (state, action) => {
                state.isSubmitting = false;
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to delete stat';
                state.apiMessage = action.payload?.message || 'Failed to delete stat';
                state.successMessage = null;
            })
            .addCase(toggleStatStatus.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(toggleStatStatus.fulfilled, (state, action) => {
                state.isLoading = false;
                const { id, is_active } = action.payload;
                state.items = state.items.map(item => item.id === id ? { ...item, is_active } : item);
                if (state.currentItem?.id === id) state.currentItem = { ...state.currentItem, is_active };
                state.successMessage = action.payload.message || 'Status toggled';
                state.apiMessage = action.payload.message || 'Status toggled';
                state.error = null;
            })
            .addCase(toggleStatStatus.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to toggle status';
                state.apiMessage = action.payload?.message || 'Failed to toggle status';
                state.successMessage = null;
            })
            .addCase(exportStats.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(exportStats.fulfilled, (state, action) => {
                state.isLoading = false;
                state.successMessage = action.payload.message || 'Exported successfully';
                state.apiMessage = action.payload.message || 'Exported successfully';
                state.error = null;
            })
            .addCase(exportStats.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to export';
                state.apiMessage = action.payload?.message || 'Failed to export';
                state.successMessage = null;
            });
    }
});

export const {
    clearError,
    clearSuccessMessage,
    clearApiMessage,
    clearState,
    clearCurrentItem,
} = statSlice.actions;

export const selectStats = (state) => state.stats?.items || [];
export const selectStatPagination = (state) => state.stats?.pagination || { current_page: 1, last_page: 1, per_page: 15, total: 0 };
export const selectCurrentStat = (state) => state.stats?.currentItem || null;
export const selectStatsLoading = (state) => state.stats?.isLoading || false;
export const selectStatsSubmitting = (state) => state.stats?.isSubmitting || false;
export const selectStatsError = (state) => state.stats?.error || null;
export const selectStatsApiMessage = (state) => state.stats?.apiMessage || null;

export default statSlice.reducer;